/**
 * New routine and Edit routine (slice 5 spec §11.2, §11.3; boards 81c, 82,
 * 82c): a dialog at 1440, a bottom sheet at 390. Name, What it does, then
 * one When panel of label-left rows (Repeats in three groups, the round day
 * buttons, At or Between, and the summary said back), then Model and Text
 * it to me. New routine lands on its Routine detail; Edit saves in place,
 * with Delete routine on the left of its footer.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crew/ui/components/alert-dialog";
import { Button } from "@crew/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@crew/ui/components/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { Field, FieldDescription, FieldLabel } from "@crew/ui/components/field";
import { Input } from "@crew/ui/components/input";
import { Link } from "@crew/ui/components/link";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@crew/ui/components/select";
import { Switch } from "@crew/ui/components/switch";
import { Textarea } from "@crew/ui/components/textarea";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { Clock } from "lucide-react";
import { Fragment, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { modelOptions } from "../settings/ai-view";
import { useModels } from "../setup/api";
import { modelLabel } from "../setup/onboarding";
import {
  useCreateRoutine,
  useDeleteRoutine,
  useEditRoutine,
  type RoutineDetail,
  type RoutineInput,
} from "./api";
import {
  blankForm,
  formOf,
  isHourly,
  REPEATS_GROUPS,
  scheduleInput,
  summary,
  textMeShown,
  withDay,
  withRepeats,
  type Repeats,
  type RoutineForm,
} from "./routine-view";

const TITLE_MAX = 80;
const INSTRUCTIONS_MAX = 4_000;

/** M T W T F S S: Monday first (Luma, Charma). */
const DAY_BUTTONS = [
  { day: 1, letter: "M", name: "Monday" },
  { day: 2, letter: "T", name: "Tuesday" },
  { day: 3, letter: "W", name: "Wednesday" },
  { day: 4, letter: "T", name: "Thursday" },
  { day: 5, letter: "F", name: "Friday" },
  { day: 6, letter: "S", name: "Saturday" },
  { day: 0, letter: "S", name: "Sunday" },
];

const REPEATS_ITEMS = REPEATS_GROUPS.flat();

/** A row of the When panel: its label in a fixed lane, then the control. */
function WhenRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-12.5 items-center gap-3 border-b border-border py-2">
      <label
        htmlFor={htmlFor}
        className="w-[58px] shrink-0 text-meta text-muted-foreground md:w-16"
      >
        {label}
      </label>
      <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
    </div>
  );
}

/**
 * Repeats, in its three groups. Editing, a reminder stays one and a
 * repeating routine stays repeating (spec §6.2), so the menu offers only those.
 */
function RepeatsMenu({
  value,
  onChange,
  only,
}: {
  value: Repeats;
  onChange: (r: Repeats) => void;
  only: "once" | "repeat" | null;
}) {
  const groups = REPEATS_GROUPS.filter((g) =>
    only === null ? true : only === "once" ? g[0].value === "once" : g[0].value !== "once",
  );
  return (
    <Select
      items={REPEATS_ITEMS}
      value={value}
      disabled={only === "once"}
      onValueChange={(v) => typeof v === "string" && onChange(v as Repeats)}
    >
      <SelectTrigger
        id="routine-repeats"
        data-routine-repeats
        className="h-8.5 w-full bg-card font-medium text-meta md:w-50"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent alignItemWithTrigger={false} align="start">
        {groups.map((group, i) => (
          <Fragment key={group[0].value}>
            {i > 0 ? <SelectSeparator /> : null}
            <SelectGroup>
              {group.map((item) => (
                <SelectItem key={item.value} value={item.value} className="py-1.5 text-body">
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </Fragment>
        ))}
      </SelectContent>
    </Select>
  );
}

function DayButtons({ days, onToggle }: { days: number[]; onToggle: (day: number) => void }) {
  return (
    <div role="group" aria-label="On" className="flex flex-wrap items-center gap-1.5">
      {DAY_BUTTONS.map(({ day, letter, name }) => {
        const on = days.includes(day);
        return (
          <button
            key={day}
            type="button"
            aria-label={name}
            aria-pressed={on}
            data-day={day}
            onClick={() => onToggle(day)}
            className={cn(
              "flex size-7.5 shrink-0 items-center justify-center rounded-full text-xs outline-none focus-visible:ring-3 focus-visible:ring-ring/50",
              on
                ? "bg-foreground font-semibold text-background"
                : "border border-input bg-card font-medium text-muted-foreground hover:text-foreground",
            )}
          >
            {letter}
          </button>
        );
      })}
    </div>
  );
}

const timeInput =
  "h-8.5 w-26 bg-card text-center text-meta [&::-webkit-calendar-picker-indicator]:hidden";

function WhenPanel({
  form,
  set,
  tz,
  editing,
  only,
}: {
  form: RoutineForm;
  set: (form: RoutineForm) => void;
  tz: string;
  editing: boolean;
  only: "once" | "repeat" | null;
}) {
  const said = summary(form, tz, new Date(), editing);
  const once = form.repeats === "once";
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-meta font-medium text-foreground">When</span>
      <div
        data-when-panel
        className="flex flex-col rounded-md border border-border bg-foreground/[0.02] px-3.5"
      >
        <WhenRow label="Repeats" htmlFor="routine-repeats">
          <RepeatsMenu
            value={form.repeats}
            onChange={(r) => set(withRepeats(form, r))}
            only={only}
          />
        </WhenRow>
        {once ? (
          <WhenRow label="Date" htmlFor="routine-date">
            <Input
              id="routine-date"
              type="date"
              value={form.on}
              onChange={(e) => set({ ...form, on: e.target.value })}
              className="h-8.5 w-40 bg-card text-meta"
            />
          </WhenRow>
        ) : (
          <WhenRow label="On">
            <DayButtons days={form.days} onToggle={(d) => set(withDay(form, d))} />
          </WhenRow>
        )}
        {isHourly(form.repeats) ? (
          <WhenRow label="Between" htmlFor="routine-from">
            <Input
              id="routine-from"
              type="time"
              aria-label="From"
              value={form.from}
              onChange={(e) => set({ ...form, from: e.target.value })}
              className={timeInput}
            />
            <span className="text-meta text-muted-foreground">and</span>
            <Input
              type="time"
              aria-label="To"
              value={form.to}
              onChange={(e) => set({ ...form, to: e.target.value })}
              className={timeInput}
            />
          </WhenRow>
        ) : (
          <WhenRow label="At" htmlFor="routine-time">
            <Input
              id="routine-time"
              type="time"
              value={form.time}
              onChange={(e) => set({ ...form, time: e.target.value })}
              className={timeInput}
            />
          </WhenRow>
        )}
        <p
          data-routine-summary
          aria-live="polite"
          className={cn(
            "-mt-px flex items-start gap-2 py-3 text-meta leading-5",
            said.ok ? "text-copy" : "text-copper",
          )}
        >
          <Clock aria-hidden className="mt-0.5 size-3.5 shrink-0" strokeWidth={1.8} />
          {said.text}
        </p>
      </div>
    </div>
  );
}

function ModelField({
  me,
  value,
  onChange,
}: {
  me: Me;
  value: string;
  onChange: (model: string) => void;
}) {
  const catalog = useModels().data;
  const ai = me.computer?.ai;
  const groups = useMemo(() => (ai ? modelOptions(catalog, ai) : []), [catalog, ai]);
  const fallback = modelLabel(catalog, ai?.model ?? null);
  const defaultLabel = fallback ? `Default · ${fallback}` : "Default";
  const items = [
    { value: "", label: defaultLabel },
    ...groups.flatMap((g) => g.models.map((m) => ({ value: m.id, label: m.label }))),
  ];
  // A routine's model the catalog no longer lists still shows as itself.
  if (value && !items.some((i) => i.value === value)) {
    items.push({ value, label: modelLabel(catalog, value) ?? value });
  }
  return (
    <Field className="min-w-0 flex-1">
      <FieldLabel htmlFor="routine-model">Model</FieldLabel>
      <Select
        items={items}
        value={value}
        onValueChange={(v) => typeof v === "string" && onChange(v)}
      >
        <SelectTrigger id="routine-model" className="h-9 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="">{defaultLabel}</SelectItem>
          </SelectGroup>
          {groups.map((g) => (
            <SelectGroup key={g.label}>
              <SelectLabel>{g.label}</SelectLabel>
              {g.models.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/** Text it to me (spec §8): the switch with a linked phone, else where to link one. */
function TextMeField({
  checked,
  onChange,
  phoneLinked,
  once,
}: {
  checked: boolean;
  onChange: (on: boolean) => void;
  phoneLinked: boolean;
  once: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5" data-text-me>
      <span className="text-meta font-medium text-foreground" id="routine-text-me-label">
        Text it to me
      </span>
      {phoneLinked ? (
        <label className="flex h-9 items-center gap-2.5 text-meta text-muted-foreground">
          <Switch
            checked={checked}
            onCheckedChange={onChange}
            aria-labelledby="routine-text-me-label"
          />
          {once ? "When it runs" : "As the instructions say"}
        </label>
      ) : (
        <p className="flex h-9 items-center text-meta text-muted-foreground" data-no-phone>
          <Link
            href="/settings/messaging"
            className="underline decoration-foreground/30 underline-offset-[3px] hover:text-foreground"
          >
            Link your phone in Settings → Messaging
          </Link>
        </p>
      )}
    </div>
  );
}

type FormMode = { kind: "new" } | { kind: "edit"; detail: RoutineDetail };

/** What a save sends: everything on New; on Edit, the whole schedule too, so a change of shape replaces the old. */
function inputOf(form: RoutineForm, phoneLinked: boolean): RoutineInput {
  return {
    title: form.title.trim(),
    instructions: form.instructions.trim(),
    ...scheduleInput(form),
    model: form.model || null,
    ...(phoneLinked ? { text_me: textMeShown(form, phoneLinked) } : {}),
  };
}

type Saving = { isPending: boolean; error: Error | null };

type FooterProps = {
  onCancel: () => void;
  onDelete?: () => void;
  ready: boolean;
  pending: boolean;
  editing: boolean;
};

/** Cancel and Save. */
function FooterButtons({
  onCancel,
  ready,
  pending,
  editing,
  className,
}: FooterProps & { className?: string }) {
  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={onCancel}
        disabled={pending}
        className={className}
      >
        Cancel
      </Button>
      <Button type="submit" disabled={!ready} className={className}>
        {pending ? "Saving…" : editing ? "Save changes" : "Save routine"}
      </Button>
    </>
  );
}

const FOOTER =
  "flex items-center justify-between gap-3 border-t border-border px-4 pt-4 pb-5 md:px-6";

/**
 * The footer: "Or ask for one in any chat." on the left (Delete routine,
 * editing), then Cancel and Save. At 390 the two buttons share the width,
 * and Delete routine goes under them.
 */
function FormFooter({ mobile, ...props }: FooterProps & { mobile: boolean }) {
  if (mobile) {
    return (
      <>
        <div className={cn(FOOTER, "*:h-10 *:flex-1")}>
          <FooterButtons {...props} />
        </div>
        {props.onDelete ? (
          <Button
            type="button"
            variant="link"
            onClick={props.onDelete}
            className="mb-4 self-center text-destructive"
            data-delete-routine
          >
            Delete routine
          </Button>
        ) : null}
      </>
    );
  }
  return (
    <div className={FOOTER}>
      {props.onDelete ? (
        <Button
          type="button"
          variant="link"
          onClick={props.onDelete}
          className="px-0 text-destructive"
          data-delete-routine
        >
          Delete routine
        </Button>
      ) : (
        <span className="flex-1 pr-4 text-meta text-muted-foreground">
          Or ask for one in any chat.
        </span>
      )}
      <div className="flex items-center gap-2">
        <FooterButtons {...props} />
      </div>
    </div>
  );
}

function RoutineFormBody({
  mode,
  me,
  tz,
  phoneLinked,
  saving,
  onSave,
  onCancel,
  onDelete,
  mobile,
}: {
  mode: FormMode;
  me: Me;
  tz: string;
  phoneLinked: boolean;
  saving: Saving;
  onSave: (input: RoutineInput) => void;
  onCancel: () => void;
  onDelete?: () => void;
  mobile: boolean;
}) {
  const editing = mode.kind === "edit";
  const [form, setForm] = useState<RoutineForm>(() =>
    mode.kind === "edit" ? formOf(mode.detail.routine, tz, new Date()) : blankForm(tz, new Date()),
  );
  const said = summary(form, tz, new Date(), editing);
  const ready =
    form.title.trim().length > 0 &&
    form.instructions.trim().length > 0 &&
    said.ok &&
    !saving.isPending;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) onSave(inputOf(form, phoneLinked));
  };
  const once = form.repeats === "once";
  return (
    <form onSubmit={submit} className="flex min-h-0 flex-col" data-routine-form={mode.kind}>
      <div className="flex flex-col gap-4.5 overflow-y-auto px-4 pb-5 md:px-6">
        <Field>
          <FieldLabel htmlFor="routine-title">Name</FieldLabel>
          <Input
            id="routine-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            maxLength={TITLE_MAX}
            placeholder="Supplier price check"
            autoFocus={!mobile}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="routine-instructions">What it does</FieldLabel>
          <Textarea
            id="routine-instructions"
            value={form.instructions}
            onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            maxLength={INSTRUCTIONS_MAX}
            rows={3}
            className="min-h-24"
            placeholder="Read this week's price emails in my Gmail, and tell me what went up. Don't reply to anyone."
          />
          <FieldDescription>
            Say what to read, what to do, and what to write. Say what it must never do.
          </FieldDescription>
        </Field>
        <WhenPanel
          form={form}
          set={setForm}
          tz={tz}
          editing={editing}
          only={mode.kind === "edit" ? mode.detail.routine.kind : null}
        />
        <div className="flex flex-col gap-4.5 md:flex-row md:items-start md:gap-4">
          <ModelField
            me={me}
            value={form.model}
            onChange={(model) => setForm({ ...form, model })}
          />
          <TextMeField
            checked={textMeShown(form, phoneLinked)}
            onChange={(textMe) => setForm({ ...form, textMe, textMeSet: true })}
            phoneLinked={phoneLinked}
            once={once}
          />
        </div>
        {saving.error ? (
          <p role="alert" className="text-meta text-destructive">
            {errorHint(saving.error)}
          </p>
        ) : null}
      </div>
      <FormFooter
        mobile={mobile}
        onCancel={onCancel}
        onDelete={onDelete}
        ready={ready}
        pending={saving.isPending}
        editing={editing}
      />
    </form>
  );
}

function DeleteConfirm({
  open,
  onOpenChange,
  title,
  onConfirm,
  pending,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onConfirm: () => void;
  pending: boolean;
  error: Error | null;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{title}”?</AlertDialogTitle>
          <AlertDialogDescription>
            It stops running and its runs go. Its chat moves to Chats, and its doc stays.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error ? <p className="text-meta text-destructive">{errorHint(error)}</p> : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={pending}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            data-confirm-delete
          >
            {pending ? "Deleting…" : "Delete routine"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** The window: a dialog at 1440, a bottom sheet at 390. */
function RoutineWindow({
  open,
  onOpenChange,
  title,
  line,
  busy,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  line: string;
  busy: boolean;
  children: ReactNode;
}) {
  const mobile = useIsMobile();
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
        <DrawerContent
          showCloseButton={!busy}
          className="max-h-[94dvh] pb-[env(safe-area-inset-bottom)]"
        >
          <DrawerHeader className="pb-4 text-left group-data-[swipe-axis=y]/drawer-popup:text-left">
            <DrawerTitle className="font-display text-[26px] leading-8 font-normal">
              {title}
            </DrawerTitle>
            <DrawerDescription>{line}</DrawerDescription>
          </DrawerHeader>
          {children}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[calc(100dvh-4rem)] gap-0 p-0 sm:max-w-140"
        showCloseButton={!busy}
      >
        <DialogHeader className="gap-1.5 px-6 pt-6 pb-5">
          <DialogTitle className="font-display text-[28px] leading-8 font-normal tracking-tight">
            {title}
          </DialogTitle>
          <DialogDescription>{line}</DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/** New routine (board 82): for the person saving; lands on its Routine detail. */
export function NewRoutineWindow({
  open,
  onOpenChange,
  me,
  phoneLinked,
  areaId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  me: Me;
  phoneLinked: boolean;
  /** Slice 6d (spec §4.2): made from Routines filtered to an Area, it's filed there. */
  areaId?: string;
}) {
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const create = useCreateRoutine();
  const change = (next: boolean) => {
    if (!next && create.isPending) return;
    if (!next) create.reset();
    onOpenChange(next);
  };
  return (
    <RoutineWindow
      open={open}
      onOpenChange={change}
      title="New routine"
      line={
        mobile
          ? "For you, with your apps and the shared ones."
          : "For you. Your Chief of Staff runs it with your apps and the shared ones."
      }
      busy={create.isPending}
    >
      {open ? (
        <RoutineFormBody
          mode={{ kind: "new" }}
          me={me}
          tz={me.workspace.timezone}
          phoneLinked={phoneLinked}
          saving={create}
          mobile={mobile}
          onCancel={() => change(false)}
          onSave={(input) =>
            create.mutate(areaId ? { ...input, area_id: areaId } : input, {
              onSuccess: ({ routine }) => navigate(`/routines/${routine.id}`),
            })
          }
        />
      ) : null}
    </RoutineWindow>
  );
}

/** Edit routine (board 81c): filled in, Save changes, and Delete routine (asked once). */
export function EditRoutineWindow({
  open,
  onOpenChange,
  me,
  detail,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  me: Me;
  detail: RoutineDetail;
}) {
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const id = detail.routine.id;
  const edit = useEditRoutine(id);
  const remove = useDeleteRoutine(id);
  const [confirming, setConfirming] = useState(false);
  const change = (next: boolean) => {
    if (!next && edit.isPending) return;
    if (!next) edit.reset();
    onOpenChange(next);
  };
  return (
    <>
      <RoutineWindow
        open={open}
        onOpenChange={change}
        title="Edit routine"
        line="For you. Changes apply from the next run."
        busy={edit.isPending}
      >
        {open ? (
          <RoutineFormBody
            mode={{ kind: "edit", detail }}
            me={me}
            tz={detail.timezone}
            phoneLinked={detail.routine.for?.phone_linked ?? false}
            saving={edit}
            mobile={mobile}
            onCancel={() => change(false)}
            onDelete={() => setConfirming(true)}
            // Not `change`: inside onSuccess the mutation still reads as pending (Devin on #341).
            onSave={(input) =>
              edit.mutate(input, {
                onSuccess: () => {
                  edit.reset();
                  onOpenChange(false);
                },
              })
            }
          />
        ) : null}
      </RoutineWindow>
      <DeleteRoutine
        open={confirming}
        onOpenChange={setConfirming}
        title={detail.routine.title}
        remove={remove}
        onDeleted={() => navigate("/routines")}
      />
    </>
  );
}

/** Delete, asked once (spec §11.2): the owner's on anyone's routine too. */
export function DeleteRoutine({
  open,
  onOpenChange,
  title,
  remove,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  remove: ReturnType<typeof useDeleteRoutine>;
  onDeleted: () => void;
}) {
  return (
    <DeleteConfirm
      open={open}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
      title={title}
      pending={remove.isPending}
      error={remove.error}
      onConfirm={() => remove.mutate(undefined, { onSuccess: onDeleted })}
    />
  );
}
